/**
 * The filter vocabulary clients browse with.
 *
 * The inner pages (`/edits/:slug` → EditFilterBar, and the per-edit chips in
 * `src/config/edits.ts`) let clients narrow by occasion, colour, fabric,
 * length, style and how soon a piece can be ready. Creators pick from this
 * same list when they create an archive post, so a dress actually shows up
 * under the filters it belongs to.
 *
 * Occasion, tags and ready-time are each stored in their own column on
 * `archive_posts`, and every one of them is optional — a post created without
 * any of these answers is perfectly valid.
 *
 * Not captured yet: the Budget and Fitting facets in EditFilterBar. They need
 * pricing and studio data that posts don't carry, so they stay disabled there.
 */

/** Occasion options — a superset of the curated edits in `config/edits.ts`. */
export const OCCASIONS = [
  'Wedding',
  'Owambe',
  'Birthday',
  'Party',
  'Prom',
  'Traditional',
  'Engagement',
  'Corporate',
] as const;

/** How long the creator usually needs. Mirrors the "Ready in" inner-page facet. */
export const READY_IN_OPTIONS = [
  '1 to 2 weeks',
  '2 to 4 weeks',
  '4 to 6 weeks',
  '6 weeks +',
] as const;

export type ReadyIn = (typeof READY_IN_OPTIONS)[number];

export interface FilterTag {
  label: string;
  /** Swatch colour, colour tags only. */
  swatch?: string;
}

export interface TagGroup {
  /** Shown as the section heading in the suggestion list. */
  name: string;
  tags: FilterTag[];
}

/** Grouped exactly as the Tags field describes them: colour, fabric, length, style. */
export const TAG_GROUPS: TagGroup[] = [
  {
    name: 'Colour',
    tags: [
      { label: 'Black', swatch: '#111111' },
      { label: 'White', swatch: '#FFFFFF' },
      { label: 'Ivory', swatch: '#F3EADA' },
      { label: 'Nude', swatch: '#D9B99B' },
      { label: 'Red', swatch: '#C8102E' },
      { label: 'Scarlet', swatch: '#E4002B' },
      { label: 'Burgundy', swatch: '#7B1A22' },
      { label: 'Pink', swatch: '#E8679A' },
      { label: 'Gold', swatch: '#C9A227' },
      { label: 'Emerald', swatch: '#046A38' },
      { label: 'Blue', swatch: '#1F4E9C' },
      { label: 'Lilac', swatch: '#B09BD6' },
    ],
  },
  {
    name: 'Fabric',
    tags: [
      { label: 'Lace' },
      { label: 'Sequin' },
      { label: 'Velvet' },
      { label: 'Satin' },
      { label: 'Silk' },
      { label: 'Tulle' },
      { label: 'Organza' },
      { label: 'Chiffon' },
      { label: 'Beaded' },
      { label: 'Aso oke' },
      { label: 'Ankara' },
      { label: 'Brocade' },
    ],
  },
  {
    name: 'Length',
    tags: [
      { label: 'Mini' },
      { label: 'Midi' },
      { label: 'Knee length' },
      { label: 'Floor length' },
      { label: 'Maxi' },
      { label: 'High low' },
    ],
  },
  {
    name: 'Style',
    tags: [
      { label: 'Corset' },
      { label: 'Fringe' },
      { label: 'Two piece' },
      { label: 'Feathers' },
      { label: 'Off shoulder' },
      { label: 'Puff sleeve' },
      { label: 'Open back' },
      { label: 'Slit' },
      { label: 'Mermaid' },
      { label: 'Ball gown' },
      { label: 'A line' },
      { label: 'Bodycon' },
      { label: 'Cape' },
      { label: 'Gele' },
      { label: 'Coral beads' },
      { label: 'Aso ebi' },
      { label: 'Agbada' },
      { label: 'Bridal hijab' },
    ],
  },
];

/** Flat lookup of every curated tag, lowercased. */
const CURATED = new Map<string, FilterTag>(
  TAG_GROUPS.flatMap((g) => g.tags.map((t) => [t.label.toLowerCase(), t] as const)),
);

export const swatchFor = (label: string): string | undefined =>
  CURATED.get(label.trim().toLowerCase())?.swatch;

// ─── Selection state ────────────────────────────────────────────────────────

export interface PostFilters {
  occasions: string[];
  tags: string[];
  readyIn: ReadyIn | null;
}

export const EMPTY_FILTERS: PostFilters = { occasions: [], tags: [], readyIn: null };

/**
 * What the "N added" pill counts: the occasion and tag answers. "Ready in" is
 * optional and sits outside the count.
 */
export const countFilters = (f: PostFilters): number => f.occasions.length + f.tags.length;
